import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  TextField,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";

import {
  createOrGetSupportThread,
  listenToSupportMessages,
  sendSupportMessage,
} from "../../redux/supportSlice";

import { fetchAllUsers } from "../../redux/authSlice";
import { useNavigate } from "react-router-dom";

const CustomerSupport: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user, allUsers } = useSelector((s: RootState) => s.auth);
  const { thread, messages } = useSelector((s: RootState) => s.support);

  const [text, setText] = useState("");
  const [autoWelcome, setAutoWelcome] = useState("");
  const [typing, setTyping] = useState(false);

  const chatRef = useRef<HTMLDivElement>(null);
  const currentUserId = user?.userId || "";

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUserId) return;

    dispatch(createOrGetSupportThread())
      .unwrap()
      .then((t) => {
        dispatch(listenToSupportMessages({ threadId: t.id }));

        const welcome = "Hey there 👋 How can we help you today?";
        let i = 0;

        const interval = setInterval(() => {
          setAutoWelcome(welcome.slice(0, i));
          i++;
          if (i > welcome.length) {
            clearInterval(interval);
            setTimeout(() => {
              setTyping(true);
              setTimeout(() => setTyping(false), 2000);
            }, 400);
          }
        }, 35);
      });
  }, [dispatch, currentUserId]);

  const supportUser = allUsers.find(
    (u) =>
      u.userId === thread?.supportId ||
      u.isSupport ||
      u.isMaster ||
      u.role?.includes("support")
  );

  const getInitials = (f?: string, l?: string) =>
    `${f?.[0] || ""}${l?.[0] || ""}`.toUpperCase();

  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, autoWelcome, typing]);

  const handleSend = () => {
    if (!text.trim()) return;
    dispatch(sendSupportMessage({ text }));
    setText("");
  };

  const formatTime = (ts: any) => {
    if (!ts) return "";
    const d =
      typeof ts === "number" ? new Date(ts) : new Date(ts.seconds * 1000);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Box
      sx={{
        height: "100vh",
        background:
          "linear-gradient(135deg, #000000 0%, #1a1a1a 40%, #bf9b30 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Chat Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : 430,
          height: isMobile ? "100%" : "90vh",
          borderRadius: isMobile ? 0 : "26px",
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          boxShadow: "0 30px 70px rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.8), rgba(191,155,48,0.6))",
            backdropFilter: "blur(15px)",
          }}
        >
          <IconButton onClick={() => navigate(-1)} sx={{ color: "#fff" }}>
            <ArrowBackIcon />
          </IconButton>

          <Avatar
            src={supportUser?.profilePicture}
            sx={{ border: "2px solid #bf9b30" }}
          >
            {getInitials(
              supportUser?.firstName,
              supportUser?.lastName
            )}
          </Avatar>

          <Typography color="#fff" fontWeight={600}>
            Support
          </Typography>
        </Box>

        {/* CHAT BODY */}
        <Box
          ref={chatRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
          }}
        >
          {autoWelcome && (
            <Box display="flex" mb={2}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: "20px",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  backdropFilter: "blur(15px)",
                }}
              >
                {autoWelcome}
              </Paper>
            </Box>
          )}

          {typing && (
            <Typography variant="caption" color="#ddd">
              Support is typing...
            </Typography>
          )}

          {messages.map((msg) => {
            const isMe = msg.senderId === currentUserId;

            return (
              <Box
                key={msg.id}
                display="flex"
                justifyContent={isMe ? "flex-end" : "flex-start"}
                mb={2}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: "75%",
                    borderRadius: "22px",
                    background: isMe
                      ? "linear-gradient(135deg, #bf9b30, #e6c766)"
                      : "rgba(255,255,255,0.12)",
                    color: isMe ? "#000" : "#fff",
                    backdropFilter: "blur(20px)",
                  }}
                >
                  {msg.text}
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      opacity: 0.7,
                      textAlign: "right",
                    }}
                  >
                    {formatTime(msg.timestamp)}
                  </Typography>
                </Paper>
              </Box>
            );
          })}
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 1,
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(15px)",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{
              input: { color: "#fff" },
              background: "rgba(255,255,255,0.1)",
              borderRadius: "14px",
            }}
          />

          <IconButton
            onClick={handleSend}
            sx={{
              background:
                "linear-gradient(135deg, #bf9b30, #e6c766)",
              color: "#000",
              "&:hover": { opacity: 0.85 },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerSupport;
